import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AppService } from '@shared/services/app.service';
import {
  AgregarItemGestionTiempoRes,
  EliminarItemsGestionTiempoReq,
  GestionTiempoRes,
} from '@core/interfaces/request-response/gestion-tiempo.dto.interface';
import { SucursalService } from '@shared/services/sucursal.service';
import { GestionTiempoApiService } from '@core/services/api/gestion-tiempo-api.service';
import { UiDialog } from '@shared/components/ui';
import { AgregrarGestionTiempo } from './agregar-gestion-tiempo/agregar-gestion-tiempo';
import { CardGestionTiempo } from '@shared/components/card-gestion-tiempo/card-gestion-tiempo';
import { GestionTiempoListenerService } from '@shared/services/websocket/gestion-tiempo-listener.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IEventNotification } from '@core/interfaces/socket.interface';
import { EVENT_GATEWAY } from '@core/enums/eventWebSocket.enum';
import { calcularTiempoRestanteGestion, formatearTiempo } from '@shared/utils/gestionTiempo.utils';
import { ToastService } from '@shared/services/toast.service';
import { TipoPagoService } from '@shared/services/tipo-pago.service';
import { TipoPagoApiService } from '@core/services/api/tipo-pago-api.service';
import { interval } from 'rxjs';

@Component({
  selector: 'app-gestion-tiempo',
  imports: [UiDialog, AgregrarGestionTiempo, CardGestionTiempo],
  templateUrl: './gestion-tiempo.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './gestion-tiempo.css',
})
export class GestionTiempo implements OnInit, OnDestroy {
  constructor() {
    // tiempo
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.now.set(Date.now()));

    effect(() => {
      if (this.sucursalService.sucursalReady() && this.appService.authReady()) {
        this.gestionTiempoListenerService.start(this.sucursalService.activeSucursal()!.id);

        this.gestionTiempoApiService.cargar().subscribe({
          next: (rs) => {
            this.gestionTiempos.set(rs);
          },
        });

        this.cargarTiposPago();
      }
    });
  }
  // Servicios
  private readonly sucursalService = inject(SucursalService);
  private readonly appService = inject(AppService);
  private readonly gestionTiempoApiService = inject(GestionTiempoApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gestionTiempoListenerService = inject(GestionTiempoListenerService);
  private readonly toastService = inject(ToastService);
  private readonly tipoPagoApiService = inject(TipoPagoApiService);
  private readonly tipoPagoService = inject(TipoPagoService);
  // datos
  private readonly now = signal<number>(Date.now());
  modal_agregar_gestion_tiempo = signal(false);
  gestionTiempos = signal<GestionTiempoRes[]>([]);

  /**
   * Computed que ordena automáticamente las gestiones por tiempo restante
   * Se actualiza cada que gestionTiempos cambie
   */
  gestionTiemposOrdenado = computed(() => {
    return [...this.gestionTiempos()].sort((a, b) => {
      const tiempoRestanteA = this.calcularTiempoRestanteGestion(a);
      const tiempoRestanteB = this.calcularTiempoRestanteGestion(b);
      return tiempoRestanteA - tiempoRestanteB;
    });
  });

  /**
   * Mapa de handlers para eventos del WebSocket
   * Mejora: Evita switch statement y permite extensión fácil
   */
  private readonly eventHandlers: Partial<
    Record<EVENT_GATEWAY, (notification: IEventNotification) => void>
  > = {
    [EVENT_GATEWAY.GESTION_TIEMPO_NUEVO]: (n) =>
      this.onCrearNuevoGestionTiempo(n.data as GestionTiempoRes),
    [EVENT_GATEWAY.GESTION_TIEMPO_AGREGAR_ITEMS]: (n) =>
      this.onAgregarItemsGestionTiempo(n.data as AgregarItemGestionTiempoRes),
    [EVENT_GATEWAY.GESTION_TIEMPO_ELIMINADO]: (n) => this.onEliminarGestionTiempo(n.data as number),
    [EVENT_GATEWAY.GESTION_TIEMPO_ELIMINAR_ITEM]: (n) =>
      this.onEliminarItemGestionTiempo(n.data as EliminarItemsGestionTiempoReq),
  };

  // metodos
  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Gestión de Tiempo',
      subtitle: 'Administra y monitorea las sesiones de tiempo de los niños',
      icon: 'bi-clock-history text-lg',
      showBackButton: true,
    });

    this.gestionTiempoListenerService.events$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        this.handleWebSocketEvent(event);
      });
  }
  ngOnDestroy(): void {
    const sucursalId = this.sucursalService.activeSucursal()?.id;
    if (sucursalId) {
      this.gestionTiempoListenerService.stop(sucursalId);
    }
  }
  private handleWebSocketEvent(notification: IEventNotification) {
    const handler = this.eventHandlers[notification.event];
    handler?.(notification);
  }
  private onCrearNuevoGestionTiempo(data: GestionTiempoRes) {
    this.gestionTiempos.update((actuales) => [data, ...actuales]);
  }

  private onAgregarItemsGestionTiempo(data: AgregarItemGestionTiempoRes) {
    this.gestionTiempos.update((actuales) =>
      actuales.map((gt) =>
        gt.id === data.id ? { ...gt, items: [...gt.items, ...data.items] } : gt,
      ),
    );
  }

  private onEliminarItemGestionTiempo(data: EliminarItemsGestionTiempoReq) {
    this.gestionTiempos.update((actuales) =>
      actuales.map((gt) => {
        if (gt.id !== data.gestion_tiempo_id) return gt;
        // Caso especial: limpiar todos los items (cuando item_ids es [-1])
        if (data.item_ids.length === 1 && data.item_ids[0] === -1) {
          return { ...gt, items: [] };
        }
        // Caso normal: eliminar solo los indicados
        return {
          ...gt,
          items: gt.items.filter((item) => !data.item_ids.includes(item.id)),
        };
      }),
    );
  }

  private onEliminarGestionTiempo(gestion_tiempo_id: number) {
    this.gestionTiempos.update((actuales) => actuales.filter((gt) => gt.id !== gestion_tiempo_id));
  }

  /**
   * Calcula el tiempo restante en segundos para una gestión de tiempo
   */
  private calcularTiempoRestanteGestion(gestionTiempo: GestionTiempoRes): number {
    return calcularTiempoRestanteGestion(gestionTiempo);
  }

  private cargarTiposPago() {
    this.tipoPagoApiService.cargarActivos().subscribe((response) => {
      this.tipoPagoService.setTiposPago(response);
    });
  }

  // Metodos para resumen diario
  tiempo_total_restante = computed(() => {
    this.now();
    const totalSegundos = this.gestionTiempos().reduce((acc, gt) => {
      return acc + this.calcularTiempoRestanteGestion(gt);
    }, 0);
    return formatearTiempo(totalSegundos);
  });

  resumenDiario = computed(() => {
    return {
      sesiones_activas: this.gestionTiempos().length,
      por_terminar: this.gestionTiempos().filter((gt) => {
        const tiempoRestante = this.calcularTiempoRestanteGestion(gt);
        return tiempoRestante > 0 && tiempoRestante <= 300; // Por terminar en los próximos 5 minutos
      }).length,
      tiempo_total_restante: this.tiempo_total_restante(),
      terminadas: this.gestionTiempos().filter((gt) => this.calcularTiempoRestanteGestion(gt) <= 0)
        .length,
    };
  });
}
